import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/index';
import { OnboardingFormFields } from '@Types/index';
import { RoutesMap } from '@Router/router.types';
import { useBulkUpdateOwnershipPercent } from '../api/ownersCenter.api';
import { getBusinessOwnerData } from '../api/util';
import { UBO_INDEX_TO_FIELD_NAME } from '../ownersCenter.consts';
import { useOwnersCenterForm } from './useOwnersCenterForm';
import { useMainOwnerPercentage } from './useMainOwnerPercentage';
import { useOwnersCenterSubmit } from './useOwnersCenterSubmit';
import { useGetBusinessOwners } from './useGetBusinessOwnersData';
import type { OwnersCenterContextValue } from '../context/OwnersCenterContext';

export const useOwnersCenterLogic = () => {
  const { businessOwners, mainOwner } = useGetBusinessOwners();
  const { navigateToSpecificPage } = useNavigationManager();
  const { getCustomerData, setCustomerData } = useBoundStore((state) => state);
  const { businessExternalId, isEmbedded, customerId } = getBusinessOwnerData();

  const formMethods = useOwnersCenterForm(businessOwners, mainOwner);
  const { handleMainOwnerPercentageChange, validateTotalPercentage } =
    useMainOwnerPercentage(formMethods);
  const { handleSubmit: submitForm, isLogicLoading } = useOwnersCenterSubmit(businessOwners);
  const { mutateAsync: bulkUpdateOwnershipPercent, isPending: isBulkUpdatePending } =
    useBulkUpdateOwnershipPercent({
      businessExternalId: businessExternalId!,
      isEmbedded,
      customerId: customerId!,
    });

  const updateAllPercentages = async () => {
    const formValues = formMethods.getValues();
    const newPrimaryPercentage = formValues[OnboardingFormFields.OWNER_PERCENTAGE] || 0;

    const updates = [
      { customerExternalId: customerId!, ownershipPercent: newPrimaryPercentage },
      ...UBO_INDEX_TO_FIELD_NAME.map((field, i) => ({
        customerExternalId: businessOwners[i]?.externalId,
        ownershipPercent: formValues[field] ?? businessOwners[i]?.ownershipPercent ?? 0,
      })).filter((entry) => entry.customerExternalId),
    ];

    await bulkUpdateOwnershipPercent(updates);

    const customerDataToUpdate = getCustomerData();
    customerDataToUpdate.customerData.customerBusinessData.ownershipPercent = newPrimaryPercentage;
    setCustomerData(customerDataToUpdate);
  };

  const handleSubmit = async () => {
    await updateAllPercentages();
    await submitForm();
  };

  const onEditClick = async (uboId: string) => {
    await updateAllPercentages();
    navigateToSpecificPage(RoutesMap.ADD_OWNER, { uboId });
  };

  const onAddClick = async () => {
    await updateAllPercentages();
    navigateToSpecificPage(RoutesMap.ADD_OWNER);
  };

  const hasUboFieldErrors = businessOwners.some((ubo) => !!ubo.fieldValidationErrors?.length);

  const contextValue: OwnersCenterContextValue = {
    businessOwners,
    mainOwner,
    onEditClick,
    onAddClick,
    handleMainOwnerPercentageChange,
    validateTotalPercentage,
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isLogicLoading || isBulkUpdatePending,
    contextValue,
    hasUboFieldErrors,
  };
};
